/**
 * `02/ Inquiries/ Board` (12:3742) — five columns over the SAME query state as the table.
 *
 * Measured: `Column` 280 wide, 16px apart; `Kanban/Card` 256 wide, 8px apart, p-12,
 * `radius/md`, `border/default`, `0 1px 1px rgba(16,24,40,.08)`; column `Header` (12:4411)
 * = an 8px stage dot + `Body/14-Med` name + a `CountChip` (`bg/surface`, h-20, px-8,
 * `radius/pill`); `Kanban/Placeholder` (6:622) = an 88px dashed block tinted with the
 * TARGET column's own stage pair — in the design it sits in Pending Confirmation and is
 * drawn in `stage/confirm/*`, so the tint follows the column, not a fixed colour;
 * `Kanban/CollapsedColumn` (6:618) = 48 wide, `bg/subtle`, a count chip and a rotated
 * label.
 *
 * ===========================================================================
 * WHAT "THE SAME QUERY STATE" MEANS, PARAMETER BY PARAMETER
 * ===========================================================================
 * `q` and `assigned` are inherited: the board is a different VIEW of one question.
 * `stage` is inherited too, and applied literally — an active stage tab renders that ONE
 * column full width. That is the honest reading of "the same query state", and it is
 * useful (a full-width Lost column is a real thing to want). The alternative — silently
 * ignoring `stage` on the board — would mean the tab strip lit up a filter that was not
 * in effect.
 * `page`/`perPage` are NOT inherited: the table's page 3 has no meaning here, and applying
 * it would show empty columns to anyone who paged before toggling. Each column loads its
 * own first 25 and says so when there are more.
 *
 * ===========================================================================
 * ⚠ THE COLUMN FOOTER TOTAL IS GONE — THE FIELD WENT, NOT THE PRINCIPLE
 * ===========================================================================
 * This section read: *"Figma's footer reads '₹2,03,000 total'. There is no per-stage sum
 * in the API, so the only available number is the sum of the LOADED cards. That number is
 * shown when it is the whole column and no amount is masked; otherwise the footer says
 * what it actually has ('25 of 41 loaded') or that amounts are hidden. A 'total' that
 * silently omitted 16 cards, or that skipped every masked row, would be worse than no
 * total: it would be a number a coordinator could quote."*
 *
 * `inquiries.expected_value` and `inquiries.currency` are dropped by migration 44 (owner
 * ruling Q6), so there is no card amount to sum. The footer keeps the HALF that was never
 * about money — "25 of 41 loaded" and the link to the full register — because that
 * disclosure is about pagination, not about a figure, and it is the reason the footer
 * still exists at all. The two money fallbacks ("Amounts hidden" / "Mixed currencies") go
 * with the sum; they were answers to questions this board can no longer be asked.
 *
 * The RULE the section states — a number that silently omits rows is worse than no number
 * — is unchanged, still true, and still binding on the next total anyone puts here. It is
 * quoted rather than deleted for exactly that reason.
 *
 * ===========================================================================
 * THE IN-COLUMN "+ NEW INQUIRY" TILE
 * ===========================================================================
 * The design draws a dashed tile at the foot of the FIRST column's card list
 * (`docs/visual/figma/06-inquiries-board.png`) — 256 wide like the cards, ~52 tall, a
 * dashed `border/default`, no fill, centred `accent/indigo` label "+ New inquiry".
 *
 * It is drawn in the New Inquiry column and NOWHERE else, and it is built that way: an
 * inquiry is created in `new` (the API's own entry stage), so a "+ New inquiry" tile at the
 * foot of Lost would promise a create that does not land there. It is not a second panel
 * either — it calls the SAME `onNewInquiry` the page's header button calls, which writes
 * `?panel=new`; the New Inquiry panel keeps one owner and one piece of state.
 *
 * It carries the header button's permission gate (`inquiries.create`) verbatim, passed in
 * as `canCreate`, and it is hidden while the column is loading or has failed: the foot of a
 * card list is not a place until there is a card list. It is a real `<button>`, so it is in
 * the tab order and reads as "New inquiry" (the `+` is decorative).
 *
 * ===========================================================================
 * DRAGGING, AND THE KEYBOARD
 * ===========================================================================
 * `@dnd-kit` with a 6px activation distance, so a click still opens the Peek instead of
 * starting a phantom drag. `KeyboardSensor` is wired as well: space picks a card up, the
 * arrow keys move it, space drops it — `closestCorners` then resolves which column it
 * landed on. Without it the board would be pointer-only, and the whole stage-change
 * gesture would be unavailable to a keyboard user even though the table's row menu can
 * do it.
 *
 * ===========================================================================
 * PHASE 2.5a — THE STAGE-COLOUR SPINE, BOARD HALF (L3)
 * ===========================================================================
 * A column now says which stage it is three times, in one colour: the 8px dot that was
 * always there, a count pill in the stage's own tint pair, and a 2px rule along the top
 * edge. The collapsed rail gets the rule and the tinted pill too — it is a column, and a
 * column that loses its identity when you fold it was never wearing one.
 *
 * WHAT WAS MEASURED, AND WHAT THE MEASUREMENT DECIDED. Every ratio below was read back
 * from the COMPILED stylesheet in a browser — `getComputedStyle` on the real utilities in
 * both themes — not computed from the hexes and hoped for.
 *
 *  · COUNT PILL — the stage pair itself, `stage/<tone>/text` on `stage/<tone>/bg`: 4.54
 *    (new) / 4.52 (quote) / 5.98 (confirm) / 4.52 (advance) / 5.30 (lost). Both halves are
 *    mode-invariant in Figma, so ONE pill clears AA 4.5:1 in BOTH themes — the numbers
 *    above are identical light and dark. It is the same pair `StagePill` renders in the
 *    table, which is the point of a spine: one source, not a board-only colour.
 *
 *  · THE LABEL IS NOT TINTED, AND THAT IS A MEASUREMENT, NOT A PREFERENCE. Twenty tints
 *    the column label itself and the brief permits it "only if it measures AA against the
 *    column background". It does not. `stage/<tone>/text` on `bg/page` reads 5.07–7.10 in
 *    light but 2.54–3.56 in DARK (`#161616`), because those foregrounds are mode-invariant
 *    and were authored for a pale tint, not for a dark page. Confirm is the worst at
 *    2.54:1. So the label stays `text/primary` and the colour is carried by the three
 *    elements that can afford it.
 *
 *  · THE 2px RULE SWAPS HALVES BETWEEN MODES, using only the two tokens the stage already
 *    owns. Light it is the strong foreground: 5.07–7.10 on `bg/page`, 4.80–6.73 on the
 *    `bg/subtle` an over-state paints — either way over the 3:1 non-text bar. Dark, that
 *    same foreground would be 2.54–3.56, so the rule shows the TINT instead: 14.82–16.14
 *    on `bg/page`, 13.49–14.70 on `bg/subtle`. No third value is invented; see
 *    `StageAccent` for the mechanism.
 */

import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { Link } from 'react-router-dom';
import { ChevronsLeftRight, MoreHorizontal } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { InquiryDto } from '../../lib/inquiries-api';
import { BOARD_STAGES, STAGES, daysInStage, stageTabLabel, stageToneClasses } from '../../domain/stages';
import type { InquiryStage, StageTone } from '../../domain/stages';
import type { CountryCode } from '../../domain/countries';
import { Button, CountryBadge, DaysPill, Spot } from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';
import { cn } from '../../lib/cn';
import { formatDayMonth } from './format';
import { useBoardColumn } from './use-inquiries';
import { BoardColumnSkeleton } from './states';
import { MenuItem } from './Toolbar';
import type { InquiryQueryState } from './query-state';

export interface BoardProps {
  countryCode: CountryCode;
  state: InquiryQueryState;
  /** WT5 — a card OPENS THE RECORD (was `onPeek`; the peek slide-over is dead). */
  onOpenRecord: (id: string) => void;
  onMove: (row: InquiryDto, targetStage: string) => void;
  canStage: boolean;
  /** `inquiries.create` — the header "+ New Inquiry" button's own gate. */
  canCreate: boolean;
  /** Goes where the header's New Inquiry action goes: the create ROUTE (WT5). */
  onNewInquiry: () => void;
  tableHrefForStage: (stage: InquiryStage) => string;
  now?: Date;
}

export function InquiryBoard({
  countryCode,
  state,
  onOpenRecord,
  onMove,
  canStage,
  canCreate,
  onNewInquiry,
  tableHrefForStage,
  now = new Date(),
}: BoardProps): React.JSX.Element {
  const columns = state.stage === null ? BOARD_STAGES : [state.stage];
  const [dragging, setDragging] = useState<InquiryDto | null>(null);

  /**
   * `lost` starts collapsed because the design draws it that way
   * (`Kanban/CollapsedColumn` is the fifth column). It is still a drop target: collapsing
   * a column to save space must not remove the only way to mark something lost.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<InquiryStage>>(() => new Set(['lost']));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const toggleCollapsed = (stage: InquiryStage): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  };

  const onDragStart = (event: DragStartEvent): void => {
    const row = event.active.data.current?.row;
    setDragging(isInquiryRow(row) ? row : null);
  };

  const onDragEnd = (event: DragEndEvent): void => {
    const row = event.active.data.current?.row;
    setDragging(null);
    if (event.over === null || !isInquiryRow(row)) return;
    onMove(row, String(event.over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setDragging(null)}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up inquiry ${String(active.id)}.`,
          onDragOver: ({ over }) =>
            over === null
              ? 'No column.'
              : `Over ${stageTabLabel(over.id as InquiryStage)}.`,
          onDragEnd: ({ over }) =>
            over === null
              ? 'Dropped outside a column. Nothing changed.'
              : `Dropped in ${stageTabLabel(over.id as InquiryStage)}.`,
          onDragCancel: () => 'Cancelled. Nothing changed.',
        },
      }}
    >
      <div className="flex items-start gap-4 overflow-x-auto pb-2">
        {columns.map((stage) =>
          collapsed.has(stage) && columns.length > 1 ? (
            <CollapsedColumn
              key={stage}
              countryCode={countryCode}
              stage={stage}
              state={state}
              onExpand={() => toggleCollapsed(stage)}
            />
          ) : (
            <BoardColumn
              key={stage}
              countryCode={countryCode}
              stage={stage}
              state={state}
              wide={columns.length === 1}
              onOpenRecord={onOpenRecord}
              canStage={canStage}
              canCreate={canCreate}
              onNewInquiry={onNewInquiry}
              collapsible={columns.length > 1}
              onCollapse={() => toggleCollapsed(stage)}
              tableHref={tableHrefForStage(stage)}
              now={now}
            />
          ),
        )}
      </div>

      {/*
       * The dragged card follows the cursor at 1.024× — the scale the design's own dragged
       * instance is drawn at (262.24 / 256, node 12:5342) — with a deeper shadow so it
       * reads as lifted off the column.
       */}
      <DragOverlay dropAnimation={null}>
        {dragging === null ? null : (
          <div className="w-64 scale-[1.024] shadow-[0_12px_16px_0_rgba(28,28,28,0.18)]">
            <CardBody row={dragging} countryCode={countryCode} now={now} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function isInquiryRow(value: unknown): value is InquiryDto {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { stage?: unknown }).stage === 'string'
  );
}

/* ──────────────────────────────────────────────────────── the spine (L3) ── */

/**
 * The 2px rule along a column's top edge — the board half of the stage-colour spine.
 *
 * ===========================================================================
 * WHY IT IS TWO NESTED SPANS AND NOT ONE `border-t-2`
 * ===========================================================================
 * Two constraints meet here, and the nesting is what satisfies both at once.
 *
 * 1. NO COMPONENT MAY NAME A STAGE COLOUR. `stageToneClasses` (`domain/stages.ts`) is the
 *    single source, and it returns a tint/foreground PAIR — never one half. Writing
 *    a per-stage top-border utility spelled here (the class name is deliberately not
 *    written out — Tailwind scans comment text and emits any class it finds, and the
 *    first draft of this sentence shipped one dead rule to production) would be a
 *    second place stage colours are spelled, which
 *    is the exact thing that map exists to prevent. So the outer span takes the pair, and
 *    the inner one paints itself `currentColor` — the foreground half — over the tint. It
 *    is the same trick the 8px dot below already uses.
 *
 * 2. THE TWO MODES WANT OPPOSITE HALVES. Measured against `bg/page`: the foreground is
 *    5.07–7.10 in light and 2.54–3.56 in dark; the tint is 1.06–1.22 in light and
 *    13.49–16.13 in dark. Neither half clears 3:1 in both. `dark:opacity-0` on the inner
 *    span is therefore not a styling flourish — it is the whole mechanism: hiding the
 *    foreground fill in dark mode uncovers the tint that was underneath it all along, and
 *    the rule ends up over 3:1 in BOTH themes without a third value being invented.
 *
 * `opacity` rather than a `dark:bg-*` override on purpose: `bg-current` and a dark
 * background utility would be two declarations of one property whose winner is stylesheet
 * order, and this repo has no `tailwind-merge` to arbitrate (`lib/cn.ts` says so in as many
 * words). Two different properties cannot conflict, so the result cannot depend on
 * emission order.
 *
 * It is `aria-hidden` and `pointer-events-none`: it repeats what the label, the dot and
 * the count pill already say, and it sits inside a drop target that must stay clickable.
 */
function StageAccent({ tone }: { tone: StageTone }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden rounded-pill',
        stageToneClasses(tone),
      )}
    >
      <span className="block size-full bg-current dark:opacity-0" />
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────── column ── */

function BoardColumn({
  countryCode,
  stage,
  state,
  wide,
  onOpenRecord,
  canStage,
  canCreate,
  onNewInquiry,
  collapsible,
  onCollapse,
  tableHref,
  now,
}: {
  countryCode: CountryCode;
  stage: InquiryStage;
  state: InquiryQueryState;
  wide: boolean;
  onOpenRecord: (id: string) => void;
  canStage: boolean;
  canCreate: boolean;
  onNewInquiry: () => void;
  collapsible: boolean;
  onCollapse: () => void;
  tableHref: string;
  now: Date;
}): React.JSX.Element {
  const query = useBoardColumn(countryCode, stage, state);
  const { isOver, setNodeRef } = useDroppable({ id: stage, disabled: !canStage });

  const rows = query.data?.data ?? [];
  const total = query.data?.total ?? 0;
  const tone = STAGES[stage].tone;

  return (
    <section
      ref={setNodeRef}
      aria-label={`${stageTabLabel(stage)} column`}
      className={cn(
        // `relative` anchors the stage rule, which is absolutely positioned so that adding
        // it costs the column no height and moves nothing below it.
        'relative flex shrink-0 flex-col gap-3 rounded-md p-3 transition-colors',
        wide ? 'w-full max-w-[880px]' : 'w-[280px]',
        isOver ? 'bg-subtle' : 'bg-transparent',
      )}
    >
      <StageAccent tone={tone} />

      <header className="flex items-center justify-between gap-2 py-0.5">
        <div className="flex min-w-0 items-center gap-2">
          {/*
           * The 8px `Dot` (12:4413), in the stage's STRONG colour.
           *
           * `stageToneClasses` is the only place stage colours are named
           * (`domain/stages.ts`), and it returns a tint/foreground PAIR. The wrapper
           * therefore carries the pair and the inner circle takes `currentColor` — the
           * foreground half — covering the tint completely. That keeps this module free of
           * any colour name, which is the rule the single stage map exists to enforce.
           *
           * LEVER 7 — `dark:opacity-0`, WHICH THIS DOT WAS MISSING AND THE RULE ABOVE
           * ALREADY HAD. `StageAccent` (70 lines up) swaps halves between modes for
           * exactly the reason stated there, and the dot renders the SAME pair on the
           * SAME background and was not doing it. Measured against `bg/page` #161616:
           *
           *   dot fill (stage/…/text)   3.36 new · 3.56 quote · 2.54 confirm ·
           *                             3.49 advance · 2.79 lost   — two under 3:1
           *   dot tint (stage/…/bg)     14.82 – 16.13              — all five clear
           *
           * Light is unaffected: the foreground still shows, at 5.07–7.10 on `bg/page`.
           * One mechanism for every stage mark in the app — this dot, the rule above,
           * and the dashboard's stage-count card — rather than three files each
           * deciding it separately.
           *
           * `StagePill`'s inner dot is DELIBERATELY not given this: it sits on the
           * pill's own tint rather than on the page, where the foreground is the half
           * that reads (4.52–5.98:1, mode-invariant). Hiding it in dark would erase a
           * dot that was never in trouble.
           */}
          <span
            aria-hidden="true"
            className={cn('inline-flex size-2 shrink-0 rounded-full', stageToneClasses(tone))}
          >
            <span className="size-2 rounded-full bg-current dark:opacity-0" />
          </span>
          {/*
           * NOT tinted. See the file header: `stage/<tone>/text` on `bg/page` measures 2.54–3.56
           * in dark mode, so a tinted label would be a dark-mode AA failure on every column.
           */}
          <h2 className="truncate text-body font-medium text-fg">{stageTabLabel(stage)}</h2>
          {/*
           * The count pill, in the stage's own pair (4.51–5.98:1, mode-invariant) instead of
           * the neutral `bg/surface` chip it used to be. This is the same pair `StagePill`
           * paints in the table — the spine is one colour per stage everywhere, not a
           * board-local decoration.
           */}
          {/*
           * L6 · LEVER 7 — the count RE-ENTERS when it changes.
           *
           * `key` is the whole mechanism and it is doing real work: React remounts
           * the element when the number differs, which restarts the keyframe. A
           * transition could not do this — the count is text content, and text
           * content is not an animatable property.
           *
           * So a card dropped into a column visibly lands in that column's total
           * instead of the number silently ticking over. Same utility, same
           * tokens, on the dashboard's stage-count cards.
           *
           * The class sits on the PILL, not on a wrapper around the digits: the
           * pill carries the stage pair, and a wrapper would have put a bare span
           * between the tint and the number for every test and reader that goes
           * looking for one.
           */}
          <span
            key={query.isPending ? 'pending' : total}
            className={cn(
              'tnum inline-flex h-5 shrink-0 items-center rounded-pill px-2 text-label font-medium',
              'motion-safe:animate-count-in',
              stageToneClasses(tone),
            )}
          >
            {query.isPending ? '·' : total}
          </span>
        </div>
        {collapsible ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              aria-label={`${stageTabLabel(stage)} column options`}
              className="flex size-6 items-center justify-center rounded-sm text-fg-muted hover:bg-subtle hover:text-fg"
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={4}
                className="z-50 w-[200px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
              >
                <MenuItem onSelect={onCollapse}>Collapse column</MenuItem>
                <DropdownMenu.Item asChild>
                  <Link
                    to={tableHref}
                    className="flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-body13 text-fg outline-none data-[highlighted]:bg-subtle"
                  >
                    Open in table
                  </Link>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : null}
      </header>

      <div className="flex min-h-[120px] flex-col gap-2">
        {query.isPending ? <BoardColumnSkeleton /> : null}

        {query.isError ? (
          <p className="rounded-sm bg-danger-bg px-3 py-2 text-label text-danger-fg">
            This column could not be loaded.
          </p>
        ) : null}

        {rows.map((row) => (
          <DraggableCard
            key={row.id}
            row={row}
            countryCode={countryCode}
            draggable={canStage}
            onOpenRecord={onOpenRecord}
            now={now}
          />
        ))}

        {/* `Kanban/Placeholder` — the drop target, tinted with this column's own pair. */}
        {isOver ? (
          <div
            aria-hidden="true"
            className={cn(
              'h-[88px] shrink-0 rounded-md border border-dashed border-current',
              stageToneClasses(tone),
            )}
          />
        ) : null}

        {!query.isPending && !query.isError && rows.length === 0 && !isOver ? (
          <EmptyColumn
            stage={stage}
            canStage={canStage}
            canCreate={canCreate}
            onNewInquiry={onNewInquiry}
          />
        ) : null}

        {/*
         * `+ New inquiry`, at the foot of the list — see the file header for why it is in
         * this column only, and why a loading or failed column does not get one.
         *
         * `rows.length > 0` is Phase 2.5a and follows the reasoning already written above:
         * the foot of a card list is not a place until there is a card list. An EMPTY New
         * column has no list to sit at the foot of, and its empty state now carries the
         * create itself — so the alternative was two create buttons stacked in one 280px
         * column, both opening the same panel.
         */}
        {stage === ENTRY_STAGE && canCreate && rows.length > 0 && !query.isPending && !query.isError ? (
          <NewInquiryTile onClick={onNewInquiry} />
        ) : null}
      </div>

      <ColumnFooter rows={rows} total={total} tableHref={tableHref} />
    </section>
  );
}

/**
 * The one column a create lands in.
 *
 * `POST /countries/:cc/inquiries` has no `stage` field — a new inquiry is `new`, always —
 * so this is the stage the tile can honestly promise, and the design draws it in exactly
 * that column.
 */
const ENTRY_STAGE: InquiryStage = 'new';

/**
 * `+ New inquiry` — the dashed create tile at the foot of the New Inquiry column.
 *
 * Full column width and `radius/md` so it lines up with the cards above it, `h-[52px]`
 * and a dashed `border/default` with no fill, per the drawing. The `+` is `aria-hidden`,
 * so the button's accessible name is "New inquiry" rather than "plus New inquiry".
 *
 * The hover pair (`accent/indigo-soft` under `accent/indigo`) is not drawn — Figma has no
 * hover state for this tile — so it reuses the pair the collapsed column already uses for
 * its own indigo highlight rather than introducing a colour.
 */
function NewInquiryTile({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[52px] w-full shrink-0 items-center justify-center gap-1 rounded-md border border-dashed border-line text-body font-medium text-indigo transition-colors hover:border-indigo hover:bg-indigo-soft"
    >
      <span aria-hidden="true">+</span>
      New inquiry
    </button>
  );
}

/* ─────────────────────────────────────────────────── the empty column (L5) ── */

/**
 * A column with nothing in it.
 *
 * It was `InquiryEmptyCard` with `Nothing in Advance Pending.` — a dot grid and a full
 * stop. Phase 2.5a keeps the card exactly as it was (same `radius/md`, same dashed
 * `border/default`, same `bg/surface`, same centred `text/secondary` body) and changes
 * only the three things lever L5 asks for: the mark, the sentence, and an action.
 *
 * WHY THIS LIVES HERE AND NOT IN `states.tsx`. `InquiryEmptyCard` is the TABLE's empty
 * state and draws the design's `dot-grid` (12:11359); the board's column wants the
 * `column` spot instead. Growing a `spot` prop on the shared card would be an edit to
 * another lever's surface mid-flight, so the board keeps its own 12 lines and the shared
 * card keeps its drawing. The container classes are deliberately identical, so folding the
 * two back together later is a prop, not a redesign.
 *
 * THE COPY IS PERMISSION-AWARE, BECAUSE THE ALTERNATIVE IS A LIE:
 *  · the New column offers the create — every inquiry really does enter at `new`, because
 *    `POST /countries/:cc/inquiries` takes no stage;
 *  · every other column names the gesture that fills it, but only when the reader can
 *    actually perform it. Telling a read-only user to "move a card here" would be an
 *    invitation to an action the API would refuse.
 *
 * "Log an inquiry", not the brief's "Log the first inquiry": the New column can be empty
 * while forty inquiries sit in the other four, and calling the next one "the first" would
 * be false on most boards that ever see this state.
 *
 * The non-entry columns get a sentence and NO button, and that is deliberate. The action
 * that fills a mid-pipeline column is a drop, not a click; the honest options for a button
 * were a link to a table view of the same empty column, or a create that would land in a
 * different one. Copy rule 7 — buttons say what they do — rules both out.
 */
function EmptyColumn({
  stage,
  canStage,
  canCreate,
  onNewInquiry,
}: {
  stage: InquiryStage;
  canStage: boolean;
  canCreate: boolean;
  onNewInquiry: () => void;
}): React.JSX.Element {
  const label = stageTabLabel(stage);
  const offersCreate = stage === ENTRY_STAGE && canCreate;

  const message = offersCreate
    ? 'Every inquiry starts here.'
    : canStage
      ? `Nothing in ${label} yet — move a card here to change its stage.`
      : `Nothing in ${label} yet.`;

  return (
    <div className="mx-auto flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-line bg-surface p-6 text-center">
      {/* Decorative and `aria-hidden` inside `Spot`; the sentence below carries the meaning. */}
      <Spot name="column" className="size-10 text-fg-muted" />
      <p className="text-body text-fg-secondary">{message}</p>
      {offersCreate ? <Button onClick={onNewInquiry}>Log an inquiry</Button> : null}
    </div>
  );
}

/**
 * The column footer — now the PARTIAL-LOAD disclosure and nothing else.
 *
 * ⚠ THE TOTAL IS GONE, AND SO ARE ITS TWO HONEST FALLBACKS. This footer led with
 * `sumMoney(rows)` over `expected_value`, and when that sum WOULD HAVE BEEN A LIE it said
 * so in words rather than printing a number: "Amounts hidden" when any row on the column
 * was masked for this seat, "Mixed currencies" when the column held more than one code.
 * Migration 44 drops `expected_value` and `currency` (owner ruling Q6), so there is no
 * sum, no masked row and no currency clash — the two fallbacks are not being weakened,
 * they are answering a question nobody can ask any more. `sumMoney` itself is deleted from
 * `format.ts` in this commit; this was its only caller.
 *
 * ⚠ AND THE EARLY RETURN CHANGED WITH IT, deliberately. It was `rows.length === 0`,
 * because a column with rows always had a total to print. The surviving content is
 * CONDITIONAL — it exists only when more rows match than are loaded — so a footer that
 * still rendered on a complete column would be an empty bar under every lane. `!partial`
 * now returns null too, and `rows.length === 0` is kept because `total > 0` with nothing
 * loaded is not a state this column should narrate.
 */
function ColumnFooter({
  rows,
  total,
  tableHref,
}: {
  rows: readonly InquiryDto[];
  total: number;
  tableHref: string;
}): React.JSX.Element | null {
  if (rows.length === 0) return null;
  const partial = total > rows.length;
  if (!partial) return null;

  return (
    <footer className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-label text-fg-muted">
      <span className="tnum">
        {rows.length} of {total} loaded
      </span>
      <Link to={tableHref} className="text-indigo hover:underline">
        see all
      </Link>
    </footer>
  );
}

/* ────────────────────────────────────────────────────────────────── the card ── */

/**
 * PHASE 2.5a · LEVER 7 — THE THREE SIGNALS A CARD CAN WEAR AT ONCE
 *
 * ===========================================================================
 * WHY THIS CARD HAD NO FOCUS RING, AND WHY THAT WAS INVISIBLE
 * ===========================================================================
 * The node below is `role="button" tabIndex={0}` — a real stop in the tab order,
 * and the only way a keyboard user reaches the record from the board. It
 * carried `outline-none`.
 *
 * `app.css` declares ONE focus treatment for the whole app inside `@layer base`.
 * `outline-none` is a utility, and Tailwind's `utilities` layer is ordered after
 * `base`, so that single class switched the app's only focus indicator off on
 * this element — with no specificity fight to notice, no warning, and no visible
 * consequence to anyone testing with a mouse. Tab through the board before this
 * change and nothing on screen tells you where you are.
 *
 * It is removed. `rounded-md` replaces it so the ring the base rule draws traces
 * the card's own 12px corner instead of the base rule's 4px floor. NO SECOND
 * FOCUS STYLE IS INTRODUCED: the fix is to stop suppressing the first one.
 *
 * ===========================================================================
 * THE SELECTED RING IS RETIRED — WEB TIER A · WT5, the slide-over kill
 * ===========================================================================
 * Lever 7 gave the card a `ring-2 ring-fg` + `aria-current` "selected" channel
 * whose single source of truth was `?peek=` — the card whose record was open
 * in the peek panel beside the board. The kill ruling deleted the panel AND
 * its URL parameter: opening a record now NAVIGATES to the record page, so at
 * no moment does the board coexist with an open record, and a persistent
 * selection ring would claim a fact that can no longer be true. The channel
 * separation reasoning (ring vs border-color vs outline — three states, three
 * CSS properties, no `tailwind-merge` to referee a collision) is preserved in
 * `finish-details.test.tsx`'s callouts should a selection ever return here.
 * HOVERED (border-swap + 2px lift) and FOCUSED (the app's one outline) remain
 * exactly as Lever 7 left them. The accessible NAME is untouched: it is what
 * the e2e specs select cards by.
 */
function DraggableCard({
  row,
  countryCode,
  draggable,
  onOpenRecord,
  now,
}: {
  row: InquiryDto;
  countryCode: CountryCode;
  draggable: boolean;
  onOpenRecord: (id: string) => void;
  now: Date;
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: row.id,
    disabled: !draggable,
    data: { row },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      aria-label={`${row.party_name ?? 'Unknown customer'} — ${row.doc_no}`}
      onClick={() => onOpenRecord(row.id)}
      onKeyDown={(event) => {
        // Enter opens; everything else — including SPACE — forwards to dnd-kit's
        // KeyboardSensor activator. The forward is EXPLICIT because the JSX spread
        // alone cannot deliver it: `{...listeners}` puts dnd-kit's own onKeyDown on
        // the element, and this later explicit prop REPLACES it — React keeps the
        // last writer. The first version of this comment claimed Space "is left to"
        // the sensor while this handler was silently swallowing it; keyboard drag
        // was dead the entire time and no pointer test could notice. An adversarial
        // review proved it from dnd-kit's source (the KeyboardSensor's activator
        // rides ONLY in `listeners`, eventName 'onKeyDown'). Enter deliberately does
        // NOT forward: dnd-kit's default start keys include Enter as well as Space,
        // and one key must open the record.
        if (event.key === 'Enter') {
          event.preventDefault();
          onOpenRecord(row.id);
          return;
        }
        listeners?.onKeyDown?.(event);
      }}
      className={cn(
        // `rounded-md`, and NO `outline-none` — see the header. The radius is here
        // purely so the app's one focus ring follows the card's corner.
        'cursor-pointer rounded-md text-left',
        /*
         * L6 — the hover lift is driven from HERE and applied to the card body inside.
         * `group` is the load-bearing part, twice over:
         *
         *  1. HOVER IS EVALUATED ON THIS NODE, WHICH NEVER MOVES. If the lift were a plain
         *     `hover:` on the element that translates, the element would slide 2px out from
         *     under a pointer resting near its bottom edge, un-hover, drop back, re-hover:
         *     a 120ms oscillation. Anchoring the hover test to a static parent makes that
         *     impossible rather than unlikely.
         *  2. IT KEEPS THE TRANSFORM OFF THE DRAGGABLE NODE. `setNodeRef` is on this div,
         *     so this is the box `@dnd-kit` measures and positions against; the transform
         *     goes on a descendant it does not measure.
         *
         * Dropping `group` while dragging disarms the lift for the duration of the drag —
         * the pointer is captured over the card the whole time, and a ghost that also
         * hovers is a second animation nobody asked for.
         */
        !isDragging && 'group',
        // The original stays in place at 40% while the DragOverlay copy follows the
        // cursor — the column keeps its height, so the board does not reflow mid-drag.
        isDragging && 'opacity-40',
      )}
    >
      <CardBody row={row} countryCode={countryCode} now={now} />
    </div>
  );
}

function CardBody({
  row,
  countryCode,
  now,
}: {
  row: InquiryDto;
  countryCode: CountryCode;
  now: Date;
}): React.JSX.Element {
  const days = daysInStage(row.stage_entered_at, now);

  return (
    /*
     * L6 — the 2px hover lift. Three properties of it are hard rules, not taste:
     *
     *  · TRANSFORM ONLY IS TRANSITIONED. The border swaps to `border/strong` on the same
     *    hover but is NOT in the transition list, because rule 4 allows transform and
     *    opacity and nothing else. `transition-transform` carries no duration class on
     *    purpose: `--default-transition-duration` is already `--crf-motion-quick` (120ms)
     *    on the token easing (`app.css`), so the plain utility is the tokenised one and
     *    there is no number to hardcode.
     *  · `motion-safe:` IS `@media (prefers-reduced-motion: no-preference)`. Note it wraps
     *    the TRANSLATE as well as the transition: a reduced-motion reader gets the stronger
     *    border and no movement at all, rather than the same 2px jump delivered instantly.
     *  · IT IS `group-hover`, driven by the static parent — see `DraggableCard`.
     *
     * The DragOverlay renders `CardBody` outside any `.group`, so the card following the
     * cursor is never lifted on top of its own 1.024× scale.
     */
    <article
      /* WT5 — the Lever-7 `ring-2 ring-fg` selected channel is retired with
       * `?peek=`: see `DraggableCard`'s header. */
      className="flex w-full flex-col gap-2 rounded-md border border-line bg-surface p-3 shadow-[0_1px_1px_0_rgba(16,24,40,0.08)] group-hover:border-line-strong motion-safe:transition-transform motion-safe:group-hover:-translate-y-0.5"
    >
      {/* `Identity` (I12:4419;6:311): name, then a muted detail line. */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="truncate text-body font-medium text-fg">
          {row.party_name ?? 'Unknown customer'}
        </p>
        {/*
         * Figma's line reads "Dinesh Gupta · Invoice module" — a contact person and a
         * work module, neither of which exists in Phase 2 (`parties` has no contact
         * column; `inquiries` has no modules column). The doc number and the subject are
         * what this row actually knows.
         */}
        {/*
         * PHASE 3a — the country leads this line, exactly as it does on the table
         * row's metadata line, so the two views of the same list put the badge in
         * the same place. `min-w-0` moves onto the text half now that the line is
         * a flex row: without it the badge would be the one shrinking.
         */}
        <p className="flex min-w-0 items-center gap-1.5">
          <CountryBadge code={countryCode} />
          <span className="min-w-0 truncate text-label text-fg-muted">
            {row.doc_no}
            {row.subject === null || row.subject === '' ? '' : ` · ${row.subject}`}
          </span>
        </p>
      </div>

      {/*
       * `Meta` (I12:4419;6:314) — WAS "a chip on the left, the amount on the right".
       *
       * ⚠ THE CHIP IS REMOVED, NOT RELABELLED, AND THE CHOICE IS THE POINT.
       * It rendered `{row.channel ?? 'inquiry'}`. `inquiries.channel` is dropped
       * (migration 43, owner ruling Q5), so the only expression left for that slot is the
       * constant `'inquiry'` — a pill that says the same word on every card in a view whose
       * every card is an inquiry. That is a shape with no information in it, and this file
       * has form for deleting those rather than keeping them for the layout's sake (see the
       * `Identity` note above, where Figma's contact-person line is not drawn because the
       * data does not exist). The stage is already the COLUMN the card sits in, so putting
       * it here would be the same fact twice.
       *
       * `justify-between` becomes `justify-end`: with one child, "between" would push the
       * amount to the LEFT edge, silently moving a number that every other view of this
       * list keeps right-aligned.
       *
       * ⚠ AND NOW THE ROW ITSELF IS GONE, because the one child went too. The amount was
       * `formatMoney(row.expected_value, row.currency)`, and migration 44 drops both
       * columns (owner ruling Q6). A `<div>` with no children is not a layout decision, it
       * is a gap: `Meta` had exactly two possible occupants and neither exists, so the
       * element is deleted rather than left holding `gap-2` open between Identity and
       * Footer. The masked marker it carried at `text-fg-secondary` — MEASURED 4.64 light /
       * 5.85 dark against a `text-fg-muted` that was 2.63:1 — is retired from
       * `renewals/masked-marker.contrast.test.tsx` in this same commit, with the reason
       * written beside its entry. The surviving sites that copied this idiom
       * (`CrfsListPage`, `RenewalsPage`, `RenewalDraftsPage`, `quotes/money.tsx`) are all
       * still measured there.
       */}

      {/* `Footer` (I12:4419;6:387): date + days chips, then the owner avatar. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="inline-flex h-5 shrink-0 items-center rounded-pill bg-subtle px-2 text-label text-fg-secondary">
            {formatDayMonth(row.created_at)}
          </span>
          <DaysPill days={days} long className="h-5 py-0" />
        </div>
        {row.assigned_to_name === null ? null : (
          /* 2.5b: the card's disc carries the photo too. `name`+`userId` replace
           * the bare `initials` so the hue identity matches every other site of
           * the same person (L4) and the photo has an id to key on; `title`
           * still carries the owner's name, so the a11y label is unchanged. */
          <UserAvatar
            name={row.assigned_to_name}
            userId={row.assigned_to}
            title={row.assigned_to_name}
          />
        )}
      </div>
    </article>
  );
}

/* ─────────────────────────────────────────────────────── collapsed column ── */

/**
 * `Kanban/CollapsedColumn` (6:618). Still a drop target — see the file header.
 *
 * PHASE 2.5a: it carries the spine too. Lost is the column that ships collapsed, so if the
 * rail were the one column with no stage colour, the board's most-used fold would also be
 * the one place the identity thread breaks. It gets the same 2px rule and the same tinted
 * count pill as an expanded column; there is no room for the 8px dot in 48px of width, and
 * the pill already carries that colour.
 *
 * The indigo `isOver` highlight is left alone. That is the DROP state — "this is where it
 * will land" — and it is deliberately one colour board-wide, so it reads the same over
 * every column. Identity and drop feedback are two different jobs.
 *
 * ===========================================================================
 * LEVER 7 — THE DROP STATE WAS UNREADABLE, IN BOTH MODES. MEASURED.
 * ===========================================================================
 * `accent/indigo-soft` #eef0fe is one of the 49 MODE-INVARIANT Figma variables,
 * so `isOver` painted the same near-white tint on a dark page as on a light one.
 * The rail's own label — the rotated stage name, 14px/500, i.e. normal-size text —
 * is `text/secondary`, which DOES flip per mode. The two together:
 *
 *   text/secondary on indigo-soft   4.10 light  ·  2.48 dark      both FAIL 4.5:1
 *
 * Dark was the worse half (#9a9a9a on #eef0fe), but light was already under the
 * bar. Two changes, no new colour between them:
 *
 *  1. `dark:bg-indigo/20` — the SAME accent, composited over the dark surface
 *     rather than replacing it, which is the technique `Button` and `toast.tsx`
 *     already use for a derived shade. Read off the painted pixel it is #2b2f4b
 *     (the browser mixes in OKLab; sRGB arithmetic would say #2b2e4b), which takes
 *     the label to 11.95:1 in dark. `--crf-accent-indigo-soft` itself is NOT given
 *     a dark value: the rule at the top of `tokens.css` forbids inventing dark halves
 *     for the mode-invariant Figma variables, and that rule still holds.
 *  2. The label goes to `text/primary` while a card is over it. That is what
 *     fixes LIGHT (4.10 → 15.03) and it is also just true: this rail is the
 *     active drop target, and the strongest foreground the token set has is the
 *     honest weight for the one column a drop is about to land in.
 *
 * The count pill is unaffected — it is an opaque stage pair over the tint, and it
 * measures the same 4.52–5.98:1 it does everywhere else.
 */
function CollapsedColumn({
  countryCode,
  stage,
  state,
  onExpand,
}: {
  countryCode: CountryCode;
  stage: InquiryStage;
  state: InquiryQueryState;
  onExpand: () => void;
}): React.JSX.Element {
  const query = useBoardColumn(countryCode, stage, state);
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const tone = STAGES[stage].tone;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        // `relative` for the stage rule only. No `overflow-hidden`: the rotated label
        // relies on being allowed to overflow a short rail, and clipping it would be a
        // silent regression bought for a 2px decoration.
        'relative flex w-12 shrink-0 flex-col items-center gap-2.5 self-stretch rounded-md py-3',
        isOver ? 'bg-indigo-soft ring-1 ring-indigo dark:bg-indigo/20' : 'bg-subtle',
      )}
    >
      <StageAccent tone={tone} />

      <span
        key={query.data?.total ?? 'pending'}
        className={cn(
          'tnum inline-flex h-5 items-center rounded-pill px-2 text-label font-medium',
          'motion-safe:animate-count-in',
          stageToneClasses(tone),
        )}
      >
        {query.data?.total ?? '·'}
      </span>
      <button
        type="button"
        onClick={onExpand}
        aria-label={`Expand ${stageTabLabel(stage)} column`}
        className={cn(
          'flex flex-1 flex-col items-center gap-2 hover:text-fg',
          // See the header: `text/secondary` on the drop tint is 4.10:1 in light.
          isOver ? 'text-fg' : 'text-fg-secondary',
        )}
      >
        <ChevronsLeftRight aria-hidden="true" className="size-3.5" />
        <span className="whitespace-nowrap text-body font-medium [writing-mode:vertical-rl]">
          {stageTabLabel(stage)}
        </span>
      </button>
    </div>
  );
}
